import * as React from "react";
import { useState } from "react";
import { Grid } from "@material-ui/core";
import { useDataProvider, useNotify, useRefresh, Button, Toolbar } from "react-admin";
import { Form } from "react-final-form";
import { useHistory } from "react-router-dom";
import { useAppDataProvider } from "../../../dataprovider";
import { CoreInfoRequestReasonTypeEnum } from "../../../Enums/CoreInfoRequestReasonTypeEnum";
import { CoreInfoRequestStatusEnum } from "../../../Enums/CoreInfoRequestStatusEnum";
import { CoreInfoRequestPutModel } from "../Model/CoreInfoRequestModel";
import { CoreInfoRequestDetailCreate } from "../Model/CoreInfoRequestDetailModel";
import { useLoading } from "../../../Components/Loading/useLoading";
import { useCoreInfoRequestStyles } from "./utils/styles";
import { Confirm } from "react-admin";
import { Update } from "@material-ui/icons";
import { CircularProgress, Card, CardContent } from "@material-ui/core";

// Import components
import CoreInfoRequestHeader from "./components/CoreInfoRequestHeader";
import BasicInformationCard from "./components/BasicInformationCard";
import TechnicalDataCard from "./components/TechnicalDataCard";
import AdditionalDetailsCard from "./components/AdditionalDetailsCard";
import AttachmentsCard from "./components/AttachmentsCard";
import CoreInfoHistoryAccordion from "./components/CoreInfoHistoryAccordion";
import PbhHistoryAccordion from "./components/PbhHistoryAccordion";

const TtCoreInfoRequestEdit = props => {
  const dataProvider = useAppDataProvider();
  const provider = useDataProvider();
  const notify = useNotify();
  const history = useHistory();
  const refresh = useRefresh();
  const { showLoading, hideLoading } = useLoading();
  const classes = useCoreInfoRequestStyles();

  // State management
  const [selectedReasons, setSelectedReasons] = useState<Number[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const submitRef = React.useRef<(() => void) | null>(null);
  const [selectedCoreInfoRequestFileType, setSelectedCoreInfoRequestFileType] = useState(0 as number);
  const [getFileGrid, setValueFileGrid] = useState({} as boolean);
  const [isFileTypeSelected, setIsFileTypeSelected] = useState(false);
  const [requestHistoryData, setRequestHistoryData] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pbhHistoryData, setPbhHistoryData] = useState<any[]>([]);
  const [pbhHistoryLoading, setPbhHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch selected reasons
  React.useEffect(() => {
    let isMounted = true;

    provider
      .getList("coreinforequestdetails", {
        pagination: { page: 1, perPage: 999 },
        sort: { field: "id", order: "DESC" },
        filter: { coreInfoRequestId__eq: props.id },
      })
      .then(response => {
        if (isMounted) {
          var selectedReason: number[] = [];
          if (response.data.length > 0) {
            response.data.forEach(element => {
              selectedReason.push(element.coreInfoRequestReasonId);
            });
            setSelectedReasons(selectedReason);
          }
        }
      })
      .catch(error => {
        if (isMounted) {
          console.error("Error fetching reasons:", error);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [props.id]);

  // Fetch Core Info Request History
  React.useEffect(() => {
    let isMounted = true;

    setHistoryLoading(true);
    dataProvider
      .getCoreInfoRequestHistoryList(props.id)
      .then(response => {
        if (isMounted) {
          setRequestHistoryData(response.data.items || []);
        }
      })
      .catch(error => {
        if (isMounted) {
          console.error("Error fetching history:", error);
          notify("Error loading history data", "error");
        }
      })
      .finally(() => {
        if (isMounted) {
          setHistoryLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [props.id]);

  // Fetch PBH Unserviceable History
  React.useEffect(() => {
    let isMounted = true;

    setPbhHistoryLoading(true);
    dataProvider
      .getPbhUnserviceableRequestHistoryList(props.id)
      .then(response => {
        if (isMounted) {
          setPbhHistoryData(response.data.items || []);
        }
      })
      .catch(error => {
        if (isMounted) {
          console.error("Error fetching PBH history:", error);
          notify("Error loading PBH history data", "error");
        }
      })
      .finally(() => {
        if (isMounted) {
          setPbhHistoryLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [props.id]);

  // Confirmation handlers
  const handleConfirmUpdate = () => {
    if (submitRef.current) {
      submitRef.current();
    }
    setConfirmOpen(false);
  };

  const handleCancelUpdate = () => {
    setConfirmOpen(false);
  };

  // Update handler
  const UpdateCoreInfoRequest = async e => {
    showLoading();

    let coreInfoRequestModel: CoreInfoRequestPutModel = new CoreInfoRequestPutModel();
    let requestReasonListCreate = [] as CoreInfoRequestDetailCreate[];
    var requestedFields = "";

    if (e.coreInfoRequestReasons !== undefined) {
      e.coreInfoRequestReasons.forEach(reason => {
        let coreInfoRequestDetailModel: CoreInfoRequestDetailCreate = new CoreInfoRequestDetailCreate();
        coreInfoRequestDetailModel.CoreInfoRequestReasonId = reason;
        requestedFields = requestedFields.concat(CoreInfoRequestReasonTypeEnum[reason] + ";");
        requestReasonListCreate.push(coreInfoRequestDetailModel);
      });
    }

    coreInfoRequestModel.Id = props.id;
    coreInfoRequestModel.RequestId = props.record.requestId;
    coreInfoRequestModel.CoreInfoRequestDetails = requestReasonListCreate;
    coreInfoRequestModel.Remark = props.record.remark;
    coreInfoRequestModel.RequestedFields = requestedFields;
    coreInfoRequestModel.Status = CoreInfoRequestStatusEnum.WaitingCustomer;
    coreInfoRequestModel.IsStatusUpdate = true;
    coreInfoRequestModel.IsCustomerUpdate = false;

    dataProvider
      .putCoreInfoRequest(coreInfoRequestModel)
      .then(result => {
        notify(`Record updated successfully`, "success", false, true);
        refresh();
      })
      .catch(result => notify(`${result}`, "error", false, true))
      .finally(() => {
        setLoading(false);
        hideLoading();
      });
  };

  // File handlers
  const changeCoreInfoRequestFileType = value => {
    if (selectedReasons.some(t => t === value.target.value)) {
      setIsFileTypeSelected(true);
      setSelectedCoreInfoRequestFileType(value.target.value);
    } else {
      setIsFileTypeSelected(false);
      setSelectedCoreInfoRequestFileType(value.target.value);
      notify(`Please Select Related File Type`, "error");
    }
  };

  const onHandleFileExecute = async () => {
    setValueFileGrid(true);
  };

  const onFileGridRefresh = async () => {
    setValueFileGrid(false);
  };

  return (
    <Form
      onSubmit={UpdateCoreInfoRequest}
      initialValues={{ ...props.record, coreInfoRequestReasons: selectedReasons }}
      render={({ handleSubmit }) => {
        submitRef.current = () => { handleSubmit(); };
        return (
          <form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CoreInfoRequestHeader record={props.record} classes={classes} />

                <BasicInformationCard
                  record={props.record}
                  selectedReasons={selectedReasons}
                  classes={classes}
                  disabled={loading}
                />

                <TechnicalDataCard record={props.record} classes={classes} mode="tt" />

                <AdditionalDetailsCard record={props.record} classes={classes} mode="tt" />

                <AttachmentsCard
                  classes={classes}
                  canEdit={false}
                  changeRequestOrderFileType={changeCoreInfoRequestFileType}
                  selectedCoreInfoRequestFileType={selectedCoreInfoRequestFileType}
                  onHandleFileExecute={onHandleFileExecute}
                  onFileGridRefresh={onFileGridRefresh}
                  getFileGrid={getFileGrid}
                  coreInfoRequestId={props.id}
                  isFileTypeSelected={isFileTypeSelected}
                />

                <CoreInfoHistoryAccordion
                  classes={classes}
                  historyLoading={historyLoading}
                  requestHistoryData={requestHistoryData}
                />

                <PbhHistoryAccordion
                  classes={classes}
                  pbhHistoryLoading={pbhHistoryLoading}
                  pbhHistoryData={pbhHistoryData}
                />

                <Card className={classes.card}>
                  <CardContent>
                    <Toolbar placeholder="" classes={classes}>
                      <Button
                        label={loading ? "Core Info Request is updating!" : "Update Core Info Request"}
                        disabled={loading}
                        size="medium"
                        variant="contained"
                        onClick={() => setConfirmOpen(true)}
                        style={{
                          margin: "8px",
                          background: "#ffc107 !important",
                        }}
                      >
                        {loading ? <CircularProgress size="2rem" color="secondary" /> : <Update />}
                      </Button>
                    </Toolbar>
                  </CardContent>
                </Card>

                <Confirm
                  isOpen={confirmOpen}
                  loading={loading}
                  title="Confirm Update"
                  content="Do you want to update?"
                  confirm="Yes"
                  cancel="No"
                  onConfirm={handleConfirmUpdate}
                  onClose={handleCancelUpdate}
                />
              </Grid>
            </Grid>
          </form>
        );
      }}
    />
  );
};

export default TtCoreInfoRequestEdit;
